import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import { KycProspectsApi } from '../tokens/kyc-prospects.token';
import {
  KycAuditDecisionRequest,
  KycClarificationResponseRequest,
  KycPreClearanceVerdictRequest,
  KycProspectClarificationDossier,
  KycProspectDecisionResponse,
  KycProspectPreClearanceDossier,
  KycProspectPromotionAuditDossier,
  KycProspectQueueRow,
} from '../models/kyc-prospect.model';

/**
 * REST implementation of {@link KycProspectsApi} — the ONE home of the `/v1/kyc/prospects` endpoints
 * (EM-1290). Every KYC prospect page consumes it through the `KYC_PROSPECTS` token; none of them wires
 * `HttpClient` itself (Rule 17).
 *
 * Errors are deliberately NOT caught here: the RFC-7807 body must reach the page's `error:` callback
 * so `extractBackendErrorMessage` can surface the backend's own message (Rule 09) — in particular the
 * `422 PROMOTION_AUDIT_PENDING` detail, which tells the reader exactly what to do next.
 */
@Injectable({ providedIn: 'root' })
export class KycProspectsRestService implements KycProspectsApi {
  private http = inject(HttpClient);

  private readonly base = API_PATHS.KYC_PROSPECTS;

  // ── T6.PR.03 ───────────────────────────────────────────────────────────────

  listPreClearance(): Observable<KycProspectQueueRow[]> {
    return this.http.get<KycProspectQueueRow[]>(`${this.base}/pre-clearance`);
  }

  getPreClearanceDossier(prospectId: number): Observable<KycProspectPreClearanceDossier> {
    return this.http.get<KycProspectPreClearanceDossier>(`${this.base}/${prospectId}/pre-clearance`);
  }

  submitPreClearanceVerdict(
    prospectId: number,
    request: KycPreClearanceVerdictRequest,
  ): Observable<KycProspectDecisionResponse> {
    return this.http.post<KycProspectDecisionResponse>(
      `${this.base}/${prospectId}/pre-clearance/verdict`,
      request,
    );
  }

  // ── T6.PR.04 ───────────────────────────────────────────────────────────────

  listClarifications(): Observable<KycProspectQueueRow[]> {
    return this.http.get<KycProspectQueueRow[]>(`${this.base}/clarifications`);
  }

  getClarificationDossier(prospectId: number): Observable<KycProspectClarificationDossier> {
    return this.http.get<KycProspectClarificationDossier>(`${this.base}/${prospectId}/clarification`);
  }

  respondToClarification(
    prospectId: number,
    request: KycClarificationResponseRequest,
  ): Observable<KycProspectDecisionResponse> {
    return this.http.post<KycProspectDecisionResponse>(
      `${this.base}/${prospectId}/clarification/respond`,
      request,
    );
  }

  // ── T6.PR.05 ───────────────────────────────────────────────────────────────

  listPromotionAudit(state: 'pending' | 'decided'): Observable<KycProspectQueueRow[]> {
    return this.http.get<KycProspectQueueRow[]>(`${this.base}/promotion-audit`, {
      params: new HttpParams().set('state', state),
    });
  }

  getPromotionAuditDossier(prospectId: number): Observable<KycProspectPromotionAuditDossier> {
    return this.http.get<KycProspectPromotionAuditDossier>(`${this.base}/${prospectId}/promotion-audit`);
  }

  clearPromotionAudit(
    prospectId: number,
    request: KycAuditDecisionRequest,
  ): Observable<KycProspectDecisionResponse> {
    return this.http.post<KycProspectDecisionResponse>(
      `${this.base}/${prospectId}/promotion-audit/clear`,
      request,
    );
  }

  rejectPromotionAudit(
    prospectId: number,
    request: KycAuditDecisionRequest,
  ): Observable<KycProspectDecisionResponse> {
    return this.http.post<KycProspectDecisionResponse>(
      `${this.base}/${prospectId}/promotion-audit/reject`,
      request,
    );
  }
}
